/** Kind of change the Novidades tab groups items by, from the CHANGELOG section titles */
export type ChangeKind = "added" | "improved" | "fixed" | "breaking" | "other";

/** One bullet of a CHANGELOG version: "- **Title** — text", with optional sub-bullets */
export interface ChangelogItem {
  title: string;
  text: string;
  details: string[];
}

/** One "### Section" of a CHANGELOG version */
export interface ChangelogSection {
  kind: ChangeKind;
  title: string;
  items: ChangelogItem[];
}

/** One "## [X.Y.Z] — date" block of the CHANGELOG */
export interface ChangelogVersion {
  version: string;
  date: string;
  body: string;
  sections: ChangelogSection[];
}

const KIND_BY_TITLE: Record<string, ChangeKind> = {
  adicionado: "added",
  melhorado: "improved",
  corrigido: "fixed",
  quebras: "breaking"
};

const VERSION_HEADER = /^## \[([^\]]+)\](?:\s*[—–-]\s*(.+))?/gm;

/**
 * Splits a version body into its sections and items. Lines that do not fit the expected shape are
 * appended to the previous item, so a wrapped bullet keeps its text.
 * @param body Markdown between two version headers
 * @returns The sections found, in file order
 */
function parseSections(body: string): ChangelogSection[] {
  const sections: ChangelogSection[] = [];
  let section: ChangelogSection | null = null;
  let item: ChangelogItem | null = null;

  body.split(/\r?\n/).forEach((line) => {
    const heading = /^###\s+(.+)$/.exec(line);
    const bullet = /^- (.+)$/.exec(line);
    const detail = /^\s{2,}- (.+)$/.exec(line);

    if (heading) {
      const title = heading[1].trim();

      section = { kind: KIND_BY_TITLE[title.toLowerCase()] || "other", title, items: [] };
      sections.push(section);
      item = null;
    } else if (bullet && section) {
      const named = /^\*\*(.+?)\*\*\s*[—–-]\s*(.+)$/.exec(bullet[1]);

      item = named ? { title: named[1], text: named[2], details: [] } : { title: "", text: bullet[1], details: [] };
      section.items.push(item);
    } else if (detail && item) {
      item.details.push(detail[1]);
    } else if (item && line.trim() && !line.trim().startsWith("---")) {
      item.text = `${item.text} ${line.trim()}`;
    }
  });

  return sections;
}

/**
 * Parses the CHANGELOG into versions, newest first as in the file.
 * @param markdown The whole CHANGELOG.md
 * @returns One entry per "## [X.Y.Z]" header
 */
export function parseChangelog(markdown: string): ChangelogVersion[] {
  const headers: { version: string; date: string; start: number; end: number }[] = [];
  const regex = new RegExp(VERSION_HEADER.source, "gm");
  let match = regex.exec(markdown);

  while (match !== null) {
    headers.push({ version: match[1], date: match[2]?.trim() || "", start: match.index, end: match.index + match[0].length });
    match = regex.exec(markdown);
  }

  return headers.map((header, position) => {
    const next = headers[position + 1];
    const body = markdown.slice(header.end, next ? next.start : markdown.length).trim();

    return { version: header.version, date: header.date, body, sections: parseSections(body) };
  });
}

/**
 * Compares two semantic versions ("1.107.13" > "1.107.9").
 * @returns Negative, zero or positive, like Array.prototype.sort expects
 */
export function compareVersions(a: string, b: string): number {
  const left = a.split(".").map(Number);
  const right = b.split(".").map(Number);

  for (let part = 0; part < 3; part += 1) {
    const diff = (left[part] || 0) - (right[part] || 0);

    if (diff !== 0) return diff;
  }

  return 0;
}

/** The version line of a version: "1.107.6" → "1.107" */
export function versionLine(version: string): string {
  return version.split(".").slice(0, 2).join(".");
}

/**
 * Lists the version lines that an interval crosses, considering only versions that exist.
 * @param versions Every version in the CHANGELOG
 * @param from First version of the interval (either order works)
 * @param to Last version of the interval
 */
export function linesBetween(versions: string[], from: string, to: string): string[] {
  const [low, high] = compareVersions(from, to) <= 0 ? [from, to] : [to, from];
  const lines = versions
    .filter((version) => compareVersions(version, low) >= 0 && compareVersions(version, high) <= 0)
    .map(versionLine);

  return Array.from(new Set(lines)).sort((a, b) => compareVersions(`${a}.0`, `${b}.0`));
}
